import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const DEFAULT_WHATSAPP_GREETING = {
  en: "Hello! I would like to enquire about a cruise on Morningstar.",
  pt: "Olá! Gostaria de informações sobre um cruzeiro no Morningstar.",
} as const;

function serverPublicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    },
  );
}

export const getWhatsappGreeting = createServerFn({ method: "GET" }).handler(
  async () => {
    try {
      const supabase = serverPublicClient();
      const { data } = await supabase
        .from("app_settings")
        .select("value_en,value_pt")
        .eq("key", "whatsapp_greeting")
        .maybeSingle();
      if (!data) return { ...DEFAULT_WHATSAPP_GREETING };
      return { en: data.value_en, pt: data.value_pt };
    } catch (err) {
      console.error("[getWhatsappGreeting]", err);
      return { ...DEFAULT_WHATSAPP_GREETING };
    }
  },
);

export const updateWhatsappGreeting = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { en: string; pt: string }) => {
    if (!data || typeof data.en !== "string" || typeof data.pt !== "string") {
      throw new Error("Invalid payload");
    }
    const en = data.en.trim();
    const pt = data.pt.trim();
    if (en.length < 5 || en.length > 500) throw new Error("EN message must be 5–500 chars");
    if (pt.length < 5 || pt.length > 500) throw new Error("PT message must be 5–500 chars");
    return { en, pt };
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roleRow, error: roleErr } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (roleErr) throw new Error(roleErr.message);
    if (!roleRow) throw new Error("Forbidden: admin role required");

    const { error } = await supabase
      .from("app_settings")
      .update({ value_en: data.en, value_pt: data.pt, updated_by: userId })
      .eq("key", "whatsapp_greeting");
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getMyAdminStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: mine } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    const isAdmin = !!mine;
    // Use admin client (service role) only to check whether any admin exists.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { count } = await supabaseAdmin
      .from("user_roles")
      .select("user_id", { count: "exact", head: true })
      .eq("role", "admin");
    return { isAdmin, hasAnyAdmin: (count ?? 0) > 0 };
  });

export const claimFirstAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { count } = await supabaseAdmin
      .from("user_roles")
      .select("user_id", { count: "exact", head: true })
      .eq("role", "admin");
    if ((count ?? 0) > 0) {
      throw new Error("An admin already exists.");
    }
    const { error } = await supabaseAdmin
      .from("user_roles")
      .insert({ user_id: userId, role: "admin" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });